// #557 — FILTRAGE TRI-ÉTAT : source unique pour TOUTES les grilles.
//
// 🔑 LE DÉFAUT QU'ON FERME
// Les filtres booléens ne proposaient que « Oui » : un checkbox décoché veut
// dire « pas de filtre », jamais « Non ». Impossible de chercher ce qui MANQUE
// — facture non payée, visite non payée, case de suivi non cochée — c'est-à-dire
// exactement ce qu'on cherche en pratique.
//
// ⛔ ET « NON » N'EST PAS « NON RENSEIGNÉ ». Un dossier dont la facture n'est pas
// payée est une relance à faire ; un dossier qui n'a pas de facture n'est pas un
// impayé. Les fondre fabriquerait une créance qui n'existe pas.
//
// ⚠️ Le prédicat historique était `if (filtre && !d.champ) return false`. `!valeur`
// est vrai pour `false`, pour `null` ET pour `undefined` : c'est la classe falsy,
// et elle rendait les deux derniers cas indiscernables. On teste donc l'IDENTITÉ,
// jamais la véracité.
//
// ⚠️ CE MODULE NE SUFFIT PAS À LUI SEUL : il ne peut distinguer que ce que la
// donnée porte encore. Un champ que le backend a déjà aplati en `bool(…)` arrive
// à deux états, et aucun filtre d'écran ne peut reconstituer le troisième. Pour
// ces champs-là, le tri-état se construit DANS LA PROJECTION (cf.
// `facture_payee_etat`) ou le filtre se déclare honnêtement à deux états.

/** Les trois états d'un filtre, plus l'absence de filtre. */
export const TRI_ETAT = {
  TOUS: "all",
  OUI: "oui",
  NON: "non",
  INCONNU: "inconnu",
};

/**
 * Un dossier passe-t-il le filtre tri-état posé sur un champ booléen ?
 *
 * @param {string}  filtre  "all" | "oui" | "non" | "inconnu" (ou vide = pas de filtre)
 * @param {*}       valeur  la valeur du champ sur la ligne
 */
export function matchTriEtat(filtre, valeur) {
  if (!filtre || filtre === TRI_ETAT.TOUS) return true;
  if (filtre === TRI_ETAT.OUI) return valeur === true;
  if (filtre === TRI_ETAT.NON) return valeur === false;
  // « non renseigné » : ni true ni false. ⚠️ `null` et `undefined` disent la même
  // chose ici — personne n'a saisi — et se traitent donc ensemble.
  if (filtre === TRI_ETAT.INCONNU) return valeur !== true && valeur !== false;
  return true;
}

/**
 * La facture a son propre vocabulaire, construit par la PROJECTION (#557) :
 * `facture_payee_etat` ∈ `payee` | `non_payee` | `sans_facture`.
 *
 * ⚠️ `facture_payee` n'existe pas en base (mesuré : 0/289 dossiers tef) — il est
 * calculé, et `bool(…)` l'aplatissait en deux valeurs AVANT que l'écran ne voie
 * quoi que ce soit. C'est pourquoi le troisième état vient du backend.
 */
export function matchEtatFacture(filtre, dossier) {
  if (!filtre || filtre === TRI_ETAT.TOUS) return true;
  const etat = etatFacture(dossier);
  if (filtre === TRI_ETAT.OUI) return etat === "payee";
  if (filtre === TRI_ETAT.NON) return etat === "non_payee";
  if (filtre === TRI_ETAT.INCONNU) return etat === "sans_facture";
  return true;
}

/**
 * L'état de facture d'une ligne, avec repli si la projection est plus ancienne
 * que l'écran.
 *
 * ⛔ Le repli ne DEVINE pas l'état manquant : depuis le booléen aplati on sait
 * seulement reconnaître « payée ». Un non-payé et un sans-facture y sont
 * confondus, donc on rend `null` — et « non » comme « non renseigné » ne
 * rendront rien, plutôt que de rendre à tort la mauvaise moitié.
 */
export function etatFacture(dossier) {
  const d = dossier || {};
  if (d.facture_payee_etat) return d.facture_payee_etat;
  return d.facture_payee ? "payee" : null;
}
